/**
 * /app/$tenantSlug/attendance/team — Team Attendance (manager / HR).
 *
 * Date-picker grid of reports (manager) / entity (HR). Scope is enforced
 * server-side in SQL; this page just renders what the API returns (a plain
 * employee with no reports gets an empty list, not a 403).
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTeamDays } from "@/hooks/use-attendance";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/team",
)({
  component: TeamAttendanceRoute,
});

function TeamAttendanceRoute() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const team = useTeamDays({ date });

  return (
    <TimeAndLeaveShell
      title="Team Attendance"
      description="Who was present, and for how long."
      actions={
        <div>
          <Label htmlFor="date">Date</Label>
          <Input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-44"
          />
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>{date}</CardTitle>
        </CardHeader>
        <CardContent>
          {team.isLoading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : team.data && team.data.days.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Worked (min)</TableHead>
                  <TableHead className="text-right">Late</TableHead>
                  <TableHead className="text-right">OT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {team.data.days.map((d) => (
                  <TableRow key={`${d.employee_id}-${d.work_date}`}>
                    <TableCell>#{d.employee_id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{d.status}</Badge>
                      {d.is_regularized && (
                        <Badge variant="secondary" className="ml-1">
                          regularized
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{d.worked_minutes}</TableCell>
                    <TableCell className="text-right">{d.late_minutes}</TableCell>
                    <TableCell className="text-right">{d.overtime_minutes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm">
              No attendance for this date (or you have no reports).
            </p>
          )}
        </CardContent>
      </Card>
    </TimeAndLeaveShell>
  );
}
